import { useEffect, useRef } from "react";
import { ADSENSE_CLIENT, AD_SLOTS } from "@/config/adsense";

/**
 * Google AdSense ad slot. Renders an `<ins class="adsbygoogle">` element that
 * the AdSense script (loaded in index.html) fills with an ad.
 *
 * Usage: <AdSlot slot="articleTop" />
 *
 * Behavior:
 * - If `AD_SLOTS[slot]` is empty in /src/config/adsense.js, nothing is rendered.
 *   This lets you deploy the code without ads until you paste real slot IDs.
 * - Renders an "Advertisement" label above the ad, per Google policy.
 * - Uses the "in-article" layout for article slots and "display" for others.
 */
export default function AdSlot({ slot, format = "in-article", className = "" }) {
  const slotId = AD_SLOTS[slot];
  const pushed = useRef(false);

  useEffect(() => {
    if (!slotId || pushed.current) return;
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch (err) {
      console.debug("adsbygoogle push failed (script not ready or blocked)", err);
    }
  }, [slotId]);

  // Nothing to render until the site owner pastes a slot ID.
  if (!slotId) return null;

  const isInArticle = format === "in-article";

  return (
    <div
      data-testid={`ad-slot-${slot}`}
      className={`my-8 not-prose ${className}`}
      aria-label="Advertisement"
    >
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-mono mb-1.5 text-center">
        Advertisement
      </div>
      {isInArticle ? (
        <ins
          className="adsbygoogle"
          style={{ display: "block", textAlign: "center" }}
          data-ad-layout="in-article"
          data-ad-format="fluid"
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={slotId}
        />
      ) : (
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={slotId}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      )}
    </div>
  );
}
